// src/views/home/Home.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "./home.scss";

import {
    FaChalkboardTeacher,
    FaSchool,
    FaChartLine,
    FaFilePdf,
    FaRocket,
    FaCheckCircle,
    FaGraduationCap,
    FaBars,
    FaTimes,
    FaRegQuestionCircle,
    FaLayerGroup,
    FaShieldAlt,
    FaCogs,
    FaArrowRight,
    FaRegMoon,
    FaRegSun,
} from "react-icons/fa";

import { HiOutlineSparkles } from "react-icons/hi2";

export default function Home() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [activeFaq, setActiveFaq] = useState(0);
    const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");

    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
        localStorage.setItem("theme", theme);
    }, [theme]);

    useEffect(() => {
        AOS.init({
            duration: 700,
            once: true,
            easing: "ease-out-cubic",
            offset: 60,
        });
    }, []);

    const stats = useMemo(
        () => [
            { value: "20k+", label: "Questões reais e itens catalogados" },
            { value: "ENEM 2009–2024", label: "Provas completas + treino por matriz e habilidades" },
            { value: "+10", label: "Olimpíadas e competições (várias bancas)" },
            { value: "Relatórios", label: "Dashboards + PDF completo por turma/aluno" },
        ],
        []
    );

    const pillars = useMemo(
        () => [
            {
                icon: <FaChalkboardTeacher />,
                title: "Professor no controle (por créditos)",
                desc: "Crie modelos de provas e listas em poucos cliques usando um banco com +20 mil questões (ENEM, vestibulares, olimpíadas e diversas bancas). Créditos organizam o uso por equipe, disciplina e período com governança e padrão pedagógico.",
            },
            {
                icon: <FaSchool />,
                title: "Instituição escala com organização",
                desc: "Cadastre professores e alunos, padronize trilhas, simulados e avaliações por coordenação e acompanhe tudo com acesso centralizado. Mais consistência, menos improviso.",
            },
            {
                icon: <FaChartLine />,
                title: "Dados que viram melhora real",
                desc: "Painel por aluno e por turma com acertos/erros, tempo, evolução e lacunas por habilidade, assunto, matéria e dificuldade. Veja exatamente onde intervir e o que priorizar para subir desempenho.",
            },
            {
                icon: <FaFilePdf />,
                title: "Relatório em PDF pronto e completo",
                desc: "Ao final, gere um PDF com as questões feitas, as que o aluno marcou como “em dúvida”, o que precisa melhorar e o gabarito tudo pronto para revisão e reuniões pedagógicas.",
            },
        ],
        []
    );

    const featureGroups = useMemo(
        () => [
            {
                title: "Banco de Questões & Treinamento",
                icon: <FaLayerGroup />,
                items: [
                    "Banco com +20 mil questões (ENEM, vestibulares e olimpíadas) para treino contínuo",
                    "Filtros avançados: matéria, assunto, habilidade, dificuldade, instituição, banca e ano",
                    "Treino dinâmico: reforça o que o aluno erra mais e acelera evolução",
                    "Painel do aluno: onde melhora, onde acerta mais, onde está travando",
                ],
            },
            {
                title: "Simulados & Avaliações",
                icon: <FaGraduationCap />,
                items: [
                    "Geração automática de simulados diferentes por área do ENEM",
                    "Simulados por vestibular/banca/instituição e por nível de aprendizado",
                    "Simulados de olimpíadas (formato por competição)",
                    "Aplicação programada: agendamento, tentativas e controle de tempo",
                ],
            },
            {
                title: "Gestão Pedagógica & Resultados",
                icon: <FaCogs />,
                items: [
                    "Relatórios por aluno, turma, série, disciplina, assunto e habilidade",
                    "Dashboard em tempo real: acerto, tempo, evolução e recorrências",
                    "Ranking por desempenho para engajar e destacar os melhores",
                    "Meta semanal do aluno (só altera após 7 dias) para criar constância e disciplina",
                ],
            },
            {
                title: "Confiabilidade & Adoção",
                icon: <FaShieldAlt />,
                items: [
                    "Experiência responsiva (celular, tablet e computador)",
                    "Onboarding rápido com modelos prontos para professores",
                    "Suporte e acompanhamento na implantação institucional",
                    "Camadas de acesso: gestão, coordenação e docentes",
                ],
            },
        ],
        []
    );

    const plans = useMemo(
        () => [
            {
                name: "Instituição Starter",
                subtitle: "Para pilotos e primeiras turmas",
                price: "Sob consulta",
                highlight: false,
                bullets: [
                    "Créditos para professores criarem provas, modelos e listas",
                    "Banco de questões + treino com filtros avançados",
                    "Relatórios por turma e por aluno",
                    "Ativação rápida",
                ],
            },
            {
                name: "Instituição Pro",
                subtitle: "Para escalar com governança",
                price: "Sob consulta",
                highlight: true,
                bullets: [
                    "Créditos por equipe + controle por disciplina e período",
                    "Simulados ENEM/vestibulares/instituições/olimpíadas completos",
                    "Agendamento de provas + regras de aplicação",
                    "Relatórios avançados + PDF completo para revisão e reuniões",
                ],
            },
            {
                name: "Rede / Premium",
                subtitle: "Multiunidade e alto volume",
                price: "Sob consulta",
                highlight: false,
                bullets: [
                    "Padronização por unidade e rede",
                    "Acompanhamento executivo (indicadores, metas e ranking por unidade)",
                    "Rotinas de intervenção e evolução por ciclo",
                    "Implantação assistida",
                ],
            },
        ],
        []
    );

    const faqs = useMemo(
        () => [
            {
                q: "Como os créditos para professores funcionam?",
                a: "A instituição define o pacote e a distribuição por equipe, disciplina e período. A cada prova/lista/modelo gerado, o sistema consome créditos conforme o tipo de aplicação assim você controla volume, padronização e uso com clareza.",
            },
            {
                q: "Dá pra aplicar provas programadas e controlar tentativas/tempo?",
                a: "Sim. Você pode agendar, definir janela de aplicação, tempo de prova e regras de tentativa. Ideal para simulados, avaliações internas e rotinas de acompanhamento.",
            },
            {
                q: "O que vem nos relatórios?",
                a: "Desempenho por habilidade, assunto, dificuldade, tempo médio e evolução. O aluno também enxerga onde mais acerta e erra, define meta semanal e acompanha o ranking. No final, ainda é possível gerar um PDF completo com as questões feitas, as marcadas como dúvida, pontos de melhoria e gabarito.",
            },
            {
                q: "A plataforma é para aluno ou para instituição?",
                a: "É institucional: gestão, coordenação e professores controlam regras, conteúdos, simulados e avaliações. Os alunos acessam o treino, metas e simulados disponibilizados pela instituição, com painel de desempenho e relatórios.",
            },
        ],
        []
    );

    const goTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setMenuOpen(false);
    };

    const openWhatsApp = () => {
        window.open("https://chat.whatsapp.com/HI3iJj6LpRO6CcCbNVzbuv", "_blank");
    };

    return (
        <div className="istudo-landing">
            {/* Background */}
            <div className="bgDecor" aria-hidden="true">
                <span className="blob blob1" />
                <span className="blob blob2" />
                <span className="blob blob3" />
                <span className="grid" />
            </div>

            {/* Header */}
            <header className={`topbar ${menuOpen ? "is-open" : ""}`}>
                <div className="container topbar__inner">
                    <a className="brand" href="/" aria-label="iStudo">
                        <img
                            src="https://i.imgur.com/l9nsCb1.png"
                            alt="iStudo"
                            className="brand__logo"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                            }}
                        />
                        <span className="brand__name">iStudo</span>
                        <span className="brand__tag">Instituições</span>

                    </a>
                    <button
                        className="themeToggle"
                        aria-label="Alternar tema"
                        onClick={() => setTheme(t => (t === "dark" ? "light" : "dark"))}
                    >
                        {theme === "dark" ? <FaRegSun /> : <FaRegMoon />}
                    </button>

                    <nav className="nav">
                        <button className="nav__link" onClick={() => goTo("recursos")}>
                            Recursos
                        </button>
                        <button className="nav__link" onClick={() => goTo("como-funciona")}>
                            Como funciona
                        </button>
                        <button className="nav__link" onClick={() => goTo("planos")}>
                            Planos
                        </button>
                        <button className="nav__link" onClick={() => goTo("faq")}>
                            FAQ
                        </button>
                        <button
                            className="nav__ghost"
                            aria-label="Alternar tema"
                            onClick={() => setTheme(t => (t === "dark" ? "light" : "dark"))}
                        >
                            {theme === "dark" ? <FaRegSun /> : <FaRegMoon />}
                        </button>


                        <button className="nav__ghost" onClick={() => navigate("/login")}>
                            Entrar
                        </button>

                        <button className="nav__cta" onClick={openWhatsApp}>
                            Falar com o time <FaArrowRight />
                        </button>
                    </nav>

                    <button
                        className="burger"
                        aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen((v) => !v)}
                    >
                        {menuOpen ? <FaTimes /> : <FaBars />}
                    </button>
                </div>

                {/* Mobile menu */}
                <div className={`mobile ${menuOpen ? "is-open" : ""}`}>
                    <div className="container mobile__inner">
                        <button className="mobile__item" onClick={() => goTo("recursos")}>
                            Recursos
                        </button>
                        <button className="mobile__item" onClick={() => goTo("como-funciona")}>
                            Como funciona
                        </button>
                        <button className="mobile__item" onClick={() => goTo("planos")}>
                            Planos
                        </button>
                        <button className="mobile__item" onClick={() => goTo("faq")}>
                            FAQ
                        </button>

                        <button className="mobile__ghost" onClick={() => navigate("/login")}>
                            Entrar
                        </button>
                        <button
                            className="mobile__ghost"
                            aria-label="Alternar tema"
                            onClick={() => setTheme(t => (t === "dark" ? "light" : "dark"))}
                        >
                            {theme === "dark" ? <FaRegSun /> : <FaRegMoon />}
                            <span style={{ marginLeft: 8 }}>
                                {theme === "dark" ? "Tema claro" : "Tema escuro"}
                            </span>
                        </button>
                        <button className="mobile__cta" onClick={openWhatsApp}>
                            Falar com o time <FaArrowRight />
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="hero">
                <div className="container hero__inner">
                    <div className="hero__copy" data-aos="fade-up">
                        <div className="kicker">
                            <span className="pill">
                                <HiOutlineSparkles />
                                Plataforma institucional
                            </span>
                            <span className="muted">Gestão • Professores • Resultados</span>
                        </div>

                        <h1 className="hero__title">
                            Avaliação e treino que{" "}
                            <span>viram resultado de verdade</span>.
                        </h1>

                        <p className="hero__subtitle">
                            O iStudo para instituições coloca professores e coordenação no controle:
                            <strong> banco com +20 mil questões</strong>, <strong>simulados</strong> e{" "}
                            <strong>relatórios</strong> que mostram exatamente onde o aluno está errando e o que
                            revisar para evoluir mais rápido.
                        </p>

                        <div className="hero__actions">
                            <button className="btn btn--primary" onClick={openWhatsApp}>
                                Solicitar demonstração <FaArrowRight />
                            </button>
                            <button className="btn btn--secondary" onClick={() => goTo("planos")}>
                                Ver planos institucionais
                            </button>
                        </div>

                        <div className="hero__checks">
                            <div className="check">
                                <FaCheckCircle /> Professores criam provas e modelos por créditos
                            </div>
                            <div className="check">
                                <FaCheckCircle /> Painel + ranking + meta semanal do aluno
                            </div>
                            <div className="check">
                                <FaCheckCircle /> PDF completo com questões, dúvidas e gabarito
                            </div>
                        </div>
                    </div>

                    <div className="hero__visual" data-aos="fade-left">
                        <div className="mock">
                            <div className="mock__header">
                                <span />
                                <span />
                                <span />
                            </div>

                            <div className="mock__body">
                                <div className="mock__card mock__card--main">
                                    <div className="mock__row">
                                        <div className="mock__icon">
                                            <FaChartLine />
                                        </div>
                                        <div className="mock__text">
                                            <div className="mock__title">Painel institucional</div>
                                            <div className="mock__desc">Evolução por turma, assunto e habilidade</div>
                                        </div>
                                        <div className="mock__badge">AO VIVO</div>
                                    </div>

                                    <div className="mock__bars">
                                        <span style={{ width: "78%" }} />
                                        <span style={{ width: "52%" }} />
                                        <span style={{ width: "86%" }} />
                                        <span style={{ width: "61%" }} />
                                    </div>

                                    <div className="mock__chips">
                                        <span>Habilidades</span>
                                        <span>Dificuldade</span>
                                        <span>Assuntos</span>
                                        <span>Tempo</span>
                                    </div>
                                </div>

                                <div className="mock__grid">
                                    <div className="mock__card">
                                        <div className="mini">
                                            <div className="mini__icon">
                                                <FaChalkboardTeacher />
                                            </div>
                                            <div className="mini__title">Provas por créditos</div>
                                            <div className="mini__desc">Modelos prontos + personalização</div>
                                        </div>
                                    </div>

                                    <div className="mock__card">
                                        <div className="mini">
                                            <div className="mini__icon">
                                                <FaFilePdf />
                                            </div>
                                            <div className="mini__title">PDF automático</div>
                                            <div className="mini__desc">Questões + dúvidas + gabarito</div>
                                        </div>
                                    </div>

                                    <div className="mock__card">
                                        <div className="mini">
                                            <div className="mini__icon">
                                                <FaGraduationCap />
                                            </div>
                                            <div className="mini__title">Simulados</div>
                                            <div className="mini__desc">ENEM • Vestibular • Olimpíadas</div>
                                        </div>
                                    </div>

                                    <div className="mock__card">
                                        <div className="mini">
                                            <div className="mini__icon">
                                                <FaSchool />
                                            </div>
                                            <div className="mini__title">Modo instituição</div>
                                            <div className="mini__desc">Governança e escala</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mock__callout">
                                    <div className="mock__calloutIcon">
                                        <FaRocket />
                                    </div>
                                    <div className="mock__calloutText">
                                        <strong>Intervenção rápida, baseada em dados.</strong>
                                        <span>Mais foco no que derruba a nota.</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="floatingNote">
                            <div className="floatingNote__title">Destaque</div>
                            <div className="floatingNote__text">
                                Gestão e coordenação identificam <strong>lacunas por habilidade</strong> e
                                direcionam ações por turma com clareza.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="stats">
                <div className="container stats__grid">
                    {stats.map((s, idx) => (
                        <div key={idx} className="stat" data-aos="fade-up" data-aos-delay={idx * 80}>
                            <div className="stat__value">{s.value}</div>
                            <div className="stat__label">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Pillars */}
            <section className="section" id="recursos">
                <div className="container">
                    <div className="sectionHead" data-aos="fade-up">
                        <div className="sectionHead__kicker">Recursos para instituições</div>
                        <h2 className="sectionHead__title">
                            Tudo que gestão, coordenação e professores precisam no mesmo lugar.
                        </h2>
                        <p className="sectionHead__subtitle">
                            Menos achismo, mais evidência: avaliações, treino e simulados com relatórios claros para melhorar desempenho com consistência.
                        </p>
                    </div>

                    <div className="cards4">
                        {pillars.map((p, idx) => (
                            <article key={idx} className="card" data-aos="fade-up" data-aos-delay={idx * 80}>
                                <div className="card__icon">{p.icon}</div>
                                <h3 className="card__title">{p.title}</h3>
                                <p className="card__desc">{p.desc}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="section section--alt" id="como-funciona">
                <div className="container">
                    <div className="sectionHead" data-aos="fade-up">
                        <div className="sectionHead__kicker">Como funciona</div>
                        <h2 className="sectionHead__title">Do planejamento ao resultado: um fluxo simples.</h2>
                        <p className="sectionHead__subtitle">
                            A instituição define regras e objetivos. O professor cria e aplica. O aluno treina com foco. E a gestão acompanha tudo com clareza.
                        </p>
                    </div>

                    <div className="steps">
                        <div className="step" data-aos="fade-up">
                            <div className="step__num">01</div>
                            <div className="step__body">
                                <h3>Cadastre e organize o acesso</h3>
                                <p>
                                    A instituição cadastra professores e alunos e define regras de uso, pacotes e governança por disciplina e equipe.
                                </p>
                            </div>
                        </div>

                        <div className="step" data-aos="fade-up" data-aos-delay="80">
                            <div className="step__num">02</div>
                            <div className="step__body">
                                <h3>Professores geram provas e listas</h3>
                                <p>
                                    Modelos prontos + personalização por assunto, habilidade, dificuldade, instituição e banca. Pronto para aplicar ou agendar.
                                </p>
                            </div>
                        </div>

                        <div className="step" data-aos="fade-up" data-aos-delay="160">
                            <div className="step__num">03</div>
                            <div className="step__body">
                                <h3>Alunos treinam com foco e constância</h3>
                                <p>
                                    Treino dinâmico por matéria e habilidade, meta semanal (só altera após 7 dias), ranking e simulados por ENEM, vestibulares e olimpíadas.
                                </p>
                            </div>
                        </div>

                        <div className="step" data-aos="fade-up" data-aos-delay="240">
                            <div className="step__num">04</div>
                            <div className="step__body">
                                <h3>Relatórios + PDF viram plano de ação</h3>
                                <p>
                                    Dashboards e PDF completo com questões feitas, dúvidas, pontos fracos e gabarito. Intervenção rápida, mensurável e fácil de revisar.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="ctaBand" data-aos="fade-up">
                        <div className="ctaBand__left">
                            <h3>Quer ver isso funcionando na sua realidade?</h3>
                            <p>
                                A demo mostra como sua instituição cria provas, libera treino e simulados, e acompanha resultados por turma e por aluno.
                            </p>
                        </div>
                        <div className="ctaBand__right">
                            <button className="btn btn--primary" onClick={openWhatsApp}>
                                Solicitar demonstração <FaArrowRight />
                            </button>
                            <button className="btn btn--secondary" onClick={() => navigate("/login")}>
                                Entrar (instituição)
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature matrix */}
            <section className="section">
                <div className="container">
                    <div className="matrix">
                        {featureGroups.map((g, idx) => (
                            <div key={idx} className="matrixCard" data-aos="fade-up" data-aos-delay={idx * 80}>
                                <div className="matrixCard__head">
                                    <div className="matrixCard__icon">{g.icon}</div>
                                    <h3 className="matrixCard__title">{g.title}</h3>
                                </div>
                                <ul className="matrixCard__list">
                                    {g.items.map((it, i) => (
                                        <li key={i}>
                                            <FaCheckCircle /> {it}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Plans */}
            <section className="section section--alt" id="planos">
                <div className="container">
                    <div className="sectionHead" data-aos="fade-up">
                        <div className="sectionHead__kicker">Planos institucionais</div>
                        <h2 className="sectionHead__title">Escolha o nível de escala. O resultado é o mesmo: controle e performance.</h2>
                        <p className="sectionHead__subtitle">
                            A contratação é institucional. Os alunos acessam o que a escola libera. Professores usam créditos para gerar avaliações, modelos e listas.
                        </p>
                    </div>

                    <div className="planGrid">
                        {plans.map((p, idx) => (
                            <article
                                key={idx}
                                className={`plan ${p.highlight ? "plan--highlight" : ""}`}
                                data-aos="fade-up"
                                data-aos-delay={idx * 90}
                            >
                                {p.highlight && <div className="plan__ribbon">Mais escolhido</div>}
                                <div className="plan__top">
                                    <h3 className="plan__name">{p.name}</h3>
                                    <div className="plan__sub">{p.subtitle}</div>
                                    <div className="plan__price">{p.price}</div>
                                </div>

                                <ul className="plan__list">
                                    {p.bullets.map((b, i) => (
                                        <li key={i}>
                                            <FaCheckCircle /> {b}
                                        </li>
                                    ))}
                                </ul>

                                <button className={`btn ${p.highlight ? "btn--primary" : "btn--secondary"} plan__btn`} onClick={openWhatsApp}>
                                    Falar com consultor <FaArrowRight />
                                </button>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="section" id="faq">
                <div className="container">
                    <div className="sectionHead" data-aos="fade-up">
                        <div className="sectionHead__kicker">FAQ</div>
                        <h2 className="sectionHead__title">Dúvidas comuns de gestores e coordenações</h2>
                        <p className="sectionHead__subtitle">
                            Se quiser, a gente mostra na demo como fica para professor, aluno e painel da gestão.
                        </p>
                    </div>

                    <div className="faq">
                        {faqs.map((f, idx) => {
                            const open = idx === activeFaq;
                            return (
                                <div
                                    key={idx}
                                    className={`faqItem ${open ? "is-open" : ""}`}
                                    data-aos="fade-up"
                                    data-aos-delay={idx * 70}
                                >
                                    <button className="faqItem__q" onClick={() => setActiveFaq(open ? -1 : idx)}>
                                        <span className="faqItem__qIcon">
                                            <FaRegQuestionCircle />
                                        </span>
                                        <span className="faqItem__qText">{f.q}</span>
                                        <span className="faqItem__qArrow">{open ? "—" : "+"}</span>
                                    </button>

                                    <div className="faqItem__a">
                                        <p>{f.a}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="finalCta" data-aos="fade-up">
                        <div className="finalCta__icon">
                            <FaRocket />
                        </div>
                        <div className="finalCta__text">
                            <h3>Pronto para transformar treino e avaliação em estratégia?</h3>
                            <p>
                                Marque uma demonstração e veja como o iStudo organiza provas, simulados, metas e relatórios para sua instituição com PDF completo para revisão.
                            </p>
                        </div>
                        <div className="finalCta__actions">
                            <button className="btn btn--primary" onClick={openWhatsApp}>
                                Solicitar demonstração <FaArrowRight />
                            </button>
                            <button className="btn btn--secondary" onClick={() => navigate("/login")}>
                                Entrar
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="container footer__inner">
                    <div className="footer__brand">
                        <img
                            src="https://i.imgur.com/l9nsCb1.png"
                            alt="iStudo"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                            }}
                        />
                        <div>
                            <div className="footer__name">iStudo</div>
                            <div className="footer__desc">Plataforma institucional para avaliação, treino e performance.</div>
                        </div>
                    </div>

                    <div className="footer__links">
                        <button onClick={() => goTo("recursos")}>Recursos</button>
                        <button onClick={() => goTo("planos")}>Planos</button>
                        <button onClick={() => goTo("faq")}>FAQ</button>
                        <button onClick={() => navigate("/login")}>Entrar</button>
                    </div>

                    <div className="footer__copy">
                        © {new Date().getFullYear()} iStudo. Todos os direitos reservados.
                    </div>
                </div>
            </footer>
        </div>
    );
}
